// Client-side blended search over the already-fetched device inventory. One
// query returns BOTH the matching device manuals (as openable rows) and the
// matching devices (as cards), so an owner can search by a remembered word
// ("depth", "Garmin", "autopilot", an install description, a PGN) and reach a
// manual without knowing the model. No network — instant and offline-friendly.

import type { UserManual, VesselDevice } from './useBoatDevices';

export interface ManualHit {
  manual: UserManual;
  device: VesselDevice;
}

export interface InventorySearchResults {
  manuals: ManualHit[];
  devices: VesselDevice[];
}

function deviceHaystack(d: VesselDevice): string {
  return [
    d.displayName,
    d.preferredName,
    d.manufacturer?.name,
    d.productInfo?.modelId,
    d.deviceClass?.name,
    d.deviceFunction?.name,
    d.configurationInfo?.installationDescription1,
    d.configurationInfo?.installationDescription2,
    d.transmitPgns?.join(' '),
    d.receivePgns?.join(' '),
  ]
    .filter((s): s is string => Boolean(s))
    .join(' ')
    .toLowerCase();
}

function manualHaystack(m: UserManual): string {
  return [m.title, m.revision, m.language]
    .filter((s): s is string => Boolean(s))
    .join(' ')
    .toLowerCase();
}

/**
 * Returns blended results for `query`. A manual is included when its own text
 * matches OR its owning device matches (so "depth" finds the depth sounder's
 * manual even if the manual's title doesn't say "depth"). Manuals are de-duped
 * by id, since the same manual is shared across every device of a model.
 */
export function searchInventory(devices: VesselDevice[], query: string): InventorySearchResults {
  const q = query.trim().toLowerCase();
  if (q === '') return { manuals: [], devices: [] };

  const matchedDevices: VesselDevice[] = [];
  const manuals: ManualHit[] = [];
  const seenManual = new Set<string>();

  for (const d of devices) {
    const deviceMatches = deviceHaystack(d).includes(q);
    if (deviceMatches) matchedDevices.push(d);
    for (const m of d.manuals ?? []) {
      if (seenManual.has(m.id)) continue; // dedupe SKU-shared manuals
      if (deviceMatches || manualHaystack(m).includes(q)) {
        manuals.push({ manual: m, device: d });
        seenManual.add(m.id);
      }
    }
  }

  return { manuals, devices: matchedDevices };
}
